import dayjs from "dayjs";
import { useEffect, useState } from "react";
import Select from "react-select";
import Datatable from "./Datatable";
import EditSale from "./EditSale";
import { useData, useDispatch } from "../../store/DataProvider";
import {
    handleApiData,
    handleLocalData,
    getMonthlySales,
    initData,
    logoutAdmin,
    unsetAdminAlert,
} from "../../store/actions";

const Admin = () => {
    const data = useData();
    const dispatch = useDispatch();
    const {
        staffOptions,
        staffObj,
        monthlySales,
        dailySales,
        adminAlert,
        shopOptions,
    } = data;

    /* the list carries a "-- New Staff --" entry for the Staff screen, which
       is not something a sale can be credited to */
    const realStaffOptions = (staffOptions || []).filter(
        o => String(o.value) !== "-1",
    );

    /* "" rather than null for all shops, so the Select has a real option to
       show rather than falling back to its placeholder */
    const ALL_SHOPS = { value: "", label: "All shops" };
    const [selectedShop, setSelectedShop] = useState(ALL_SHOPS);

    useEffect(() => {
        getMonthlySales(null, dispatch);
    }, []);

    useEffect(() => {
        logoutAdmin(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (staffOptions.length === 0) {
            initData(dispatch);
        }
    }, [staffOptions, dispatch]);

    useEffect(() => {
        const { message } = adminAlert;
        if (message) {
            window.alert(message);
            unsetAdminAlert(dispatch);
            getMonthlySales(null, dispatch);
        }
    }, [adminAlert, dispatch]);

    /* Unsure why cloning (...) necessary here
     * think it has to do with handling undefined async data */
    const totals = { ...monthlySales.totals };
    const { options: monthOptions, selected } = { ...monthlySales.selectObj };

    const [selectedMonth, setSelectedMonth] = useState();
    const [selectedDay, setSelectedDay] = useState();

    useEffect(() => {
        setSelectedMonth(selected);
    }, [selected]);

    /* Which day the server opened on, so the row is marked on arrival and
       after a change of month. Switching day is handled locally and does not
       touch this, so a click is not undone by it. */
    useEffect(() => {
        setSelectedDay(dailySales?.selectedDay);
    }, [dailySales?.selectedDay]);

    /* A rule where the summary begins, so it reads as a block beneath the day
       list rather than as one more line of it. The server marks whichever row
       opens the summary: the shop's total when one is chosen, the month's
       otherwise. Heavier than the 1px line between the two rows, which is
       what makes it divide the block rather than part the rows. */
    const totalsRowStyles = [
        {
            when: row => row.ruleAbove,
            style: { borderTop: "2px solid rgba(0, 0, 0, .26)" },
        },
    ];

    /* the Totals row carries no day, so it never matches */
    const dayRowStyles = [
        {
            when: row => row.day === selectedDay,
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
    ];

    const onSelectMonth = opt => {
        setSelectedMonth(opt);
        getMonthlySales(opt.value, dispatch, selectedShop.value);
    };

    /* The shop holds across a change of month, being context for the screen
       rather than part of the month being looked at. The day is let go: the
       one showing may have no sales at this shop, and the server picks the
       latest that does. */
    const onSelectShop = opt => {
        setSelectedShop(opt);
        setSelectedDay(null);
        getMonthlySales(selectedMonth?.value, dispatch, opt.value);
    };

    const handleDailyClick = row => {
        const { day } = row;
        const { format } = dailySales;
        const title = dayjs(day).format(format);
        handleLocalData(
            { type: "switchDay", payload: { day, title } },
            dispatch,
        );
        setSelectedDay(day);
    };

    /* Which sale the editor below the day list has open. Held as the row
       rather than the id: the row carries the ids, the rate and the line
       prices that the form has to put back. */
    const [editingSale, setEditingSale] = useState(null);

    /* the sale being edited, so the form and the list agree about which one
       is in hand */
    const saleRowStyles = [
        {
            when: row => Boolean(editingSale) && row._id === editingSale._id,
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
    ];

    /* the day may have moved out from under the editor, and the sale it was
       showing may not be in the list any more */
    useEffect(() => {
        setEditingSale(null);
    }, [selectedDay, selectedShop, selectedMonth?.value]);

    const refreshMonth = async () => {
        const day = selectedDay ? `|${selectedDay}` : "";
        getMonthlySales(
            `${selectedMonth.value}${day}`,
            dispatch,
            selectedShop.value,
        );
    };

    const handleSaveSale = async payload => {
        setEditingSale(null);
        /* awaited, so the refresh below cannot race the write and hand back
           the sale as it was */
        await handleApiData(payload, dispatch, "updateSale");
        refreshMonth();
    };

    /* Deleting from the form, on a sale deliberately opened, rather than from
       a double-click on the row: a double-click fires a single click first,
       so once a click opens the editor every delete was opening it too. The
       confirm names the sale, a bare "this sale" being no help at all when
       the thing it deletes cannot be got back. */
    const handleDeleteSale = async () => {
        const { _id, staff, sale, time } = editingSale;
        if (
            !window.confirm(
                `Delete the ${sale} sale by ${staff} at ${time}?\n\nThis cannot be undone.`,
            )
        ) {
            return;
        }
        setEditingSale(null);
        await handleApiData({ saleId: _id }, dispatch, "deleteSale");
        refreshMonth();
    };

    return (
        <div className="admin-wrapper">
            <div className="admin-column">
                <div className="select-wrapper">
                    <div className="spacer-div">&nbsp;</div>
                    {/* the month and the shop are both context for everything
                        below, so they sit together above it */}
                    <div className="month-shop-selects">
                        <div className="month-select">
                            <Select
                                value={selectedMonth}
                                onChange={opt => onSelectMonth(opt)}
                                options={monthOptions}
                            />
                        </div>
                        <div className="shop-filter-select">
                            <Select
                                value={selectedShop}
                                onChange={opt => onSelectShop(opt)}
                                options={[ALL_SHOPS, ...(shopOptions || [])]}
                            />
                        </div>
                    </div>
                </div>
                <div className="data-wrapper">
                    <Datatable
                        settings={monthlySales}
                        clickFunc={handleDailyClick}
                        conditionalRowStyles={dayRowStyles}
                    />
                </div>
                <div
                    className="admin-gutter-right"
                    style={{
                        flex: "0 1 15px",
                        backgroundColor: "white",
                        padding: "7px",
                    }}
                >
                    <Datatable
                        settings={totals}
                        loading="Loading ..."
                        conditionalRowStyles={totalsRowStyles}
                    />
                </div>
            </div>

            <div className="admin-column">
                {/* the day list scrolls inside its own box so the editor
                    below holds its place, the way the expense types pane
                    does: on a short screen the form would otherwise open
                    below the fold and clicking a row would look as though
                    nothing had happened */}
                <div
                    className="data-wrapper daily-sales-pane"
                    style={{ borderRight: 0 }}
                >
                    <div className="daily-sales-list">
                        <Datatable
                            settings={dailySales}
                            clickFunc={row => setEditingSale(row)}
                            conditionalRowStyles={saleRowStyles}
                        />
                    </div>
                    <EditSale
                        sale={editingSale}
                        staffOptions={realStaffOptions}
                        staffObj={staffObj}
                        shopOptions={shopOptions}
                        onCancel={() => setEditingSale(null)}
                        onSave={handleSaveSale}
                        onDelete={handleDeleteSale}
                    />
                </div>
                <div
                    className="admin-gutter-right"
                    style={{ flex: "0 1 0px", backgroundColor: "white" }}
                ></div>
            </div>
        </div>
    );
};

export default Admin;
